import {ChatMessage, CreateSessionResponse, DiagramSession, DiagramToolCall, SessionEntity, SessionUsage, SyncEntitiesResponse, ToolResult} from "../types/chatTypes";
import {clearDiagram, connectEntities, createEntity, getCameraPosition, getCameraForSync, listEntities, modifyConnection, modifyEntity, removeEntity} from "./entityBridge";
import {v4 as uuidv4} from 'uuid';
import log from 'loglevel';

const logger = log.getLogger('diagramAI');
logger.setLevel('debug');

// Session management
let currentSessionId: string | null = null;

// Model management
export type AIProvider = 'claude' | 'ollama' | 'cloudflare';

export interface ModelInfo {
    id: string;
    name: string;
    description: string;
    provider: AIProvider;
    contextLimit: number;  // Maximum context window size in tokens
}

const AVAILABLE_MODELS: ModelInfo[] = [
    // Claude models - 200K context
    {
        id: 'claude-sonnet-4-20250514',
        name: 'Claude Sonnet 4',
        description: 'Balanced performance and speed (default)',
        provider: 'claude',
        contextLimit: 200000
    },
    {
        id: 'claude-opus-4-20250514',
        name: 'Claude Opus 4',
        description: 'Most capable, best for complex tasks',
        provider: 'claude',
        contextLimit: 200000
    },
    {
        id: 'claude-haiku-3-5-20241022',
        name: 'Claude Haiku 3.5',
        description: 'Fastest responses, good for simple tasks',
        provider: 'claude',
        contextLimit: 200000
    },
    // Cloudflare Workers AI models - with tool support
    {
        id: '@cf/mistralai/mistral-small-3.1-24b-instruct',
        name: 'Mistral Small 3.1 (CF)',
        description: 'Best CF model - supports diagram tools',
        provider: 'cloudflare',
        contextLimit: 32000
    },
    {
        id: '@hf/nousresearch/hermes-2-pro-mistral-7b',
        name: 'Hermes 2 Pro (CF)',
        description: 'Lightweight - supports diagram tools',
        provider: 'cloudflare',
        contextLimit: 8000
    },
    // Cloudflare models WITHOUT tool support - chat only
    {
        id: '@cf/meta/llama-3.3-70b-instruct-fp8-fast',
        name: 'Llama 3.3 70B (CF)',
        description: 'Powerful but NO tool support',
        provider: 'cloudflare',
        contextLimit: 128000
    },
    {
        id: '@cf/meta/llama-3.1-8b-instruct',
        name: 'Llama 3.1 8B (CF)',
        description: 'Fast/cheap but NO tool support',
        provider: 'cloudflare',
        contextLimit: 128000
    },
    {
        id: '@cf/deepseek-ai/deepseek-r1-distill-qwen-32b',
        name: 'DeepSeek R1 (CF)',
        description: 'Reasoning but NO tool support',
        provider: 'cloudflare',
        contextLimit: 32000
    },
    {
        id: '@cf/qwen/qwen2.5-coder-32b-instruct',
        name: 'Qwen 2.5 Coder (CF)',
        description: 'Code-focused but NO tool support',
        provider: 'cloudflare',
        contextLimit: 32000
    },
    // Ollama models (local) - context depends on local config, using defaults
    {
        id: 'llama3.1',
        name: 'Llama 3.1',
        description: 'Local model with function calling support',
        provider: 'ollama',
        contextLimit: 128000
    },
    {
        id: 'mistral',
        name: 'Mistral',
        description: 'Fast local model with good tool support',
        provider: 'ollama',
        contextLimit: 32000
    },
    {
        id: 'qwen2.5',
        name: 'Qwen 2.5',
        description: 'Capable local model with function calling',
        provider: 'ollama',
        contextLimit: 32000
    }
];

let currentModelId: string = '@cf/mistralai/mistral-small-3.1-24b-instruct';

/**
 * Get the API endpoint for the current model's provider
 */
function getApiEndpoint(): string {
    const model = getCurrentModel();
    if (model.provider === 'ollama') {
        return '/api/ollama/v1/messages';
    }
    if (model.provider === 'cloudflare') {
        return '/api/cloudflare/v1/messages';
    }
    return '/api/claude/v1/messages';
}

/**
 * Get available models
 */
export function getAvailableModels(): ModelInfo[] {
    return [...AVAILABLE_MODELS];
}

/**
 * Get current model
 */
export function getCurrentModel(): ModelInfo {
    return AVAILABLE_MODELS.find(m => m.id === currentModelId) || AVAILABLE_MODELS[0];
}

/**
 * Set current model
 */
export function setCurrentModel(modelIdOrName: string): boolean {
    // Try to find by exact ID first
    let model = AVAILABLE_MODELS.find(m => m.id === modelIdOrName);

    // If not found, try to match by display name (case-insensitive)
    if (!model) {
        const searchLower = modelIdOrName.toLowerCase();
        model = AVAILABLE_MODELS.find(m =>
            m.name.toLowerCase() === searchLower ||
            m.name.toLowerCase().includes(searchLower) ||
            m.id.toLowerCase().includes(searchLower)
        );
    }

    if (model) {
        currentModelId = model.id;
        logger.info('Model changed to:', model.name, `(${model.id})`);
        return true;
    }
    logger.warn('Invalid model ID or name:', modelIdOrName);
    return false;
}

/**
 * Create a new session or resume existing one for a diagram
 */
export async function createOrResumeSession(diagramId: string): Promise<{ session: DiagramSession; isNew: boolean }> {
    const response = await fetch('/api/session/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ diagramId })
    });

    if (!response.ok) {
        throw new Error(`Failed to create session: ${response.status}`);
    }

    const data: CreateSessionResponse = await response.json();
    currentSessionId = data.session.id;
    return data;
}

/**
 * Get current session ID
 */
export function getCurrentSessionId(): string | null {
    return currentSessionId;
}

/**
 * Sync entities to the current session
 */
export async function syncEntitiesToSession(entities: SessionEntity[]): Promise<void> {
    if (!currentSessionId) {
        console.warn('No active session to sync entities to');
        return;
    }

    const response = await fetch(`/api/session/${currentSessionId}/sync`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entities })
    });

    if (!response.ok) {
        console.error('Failed to sync entities:', response.status);
    }
}

/**
 * Sync camera position to the current session
 */
export async function syncCameraToSession(): Promise<void> {
    if (!currentSessionId) {
        return;
    }

    const cameraPosition = await getCameraForSync();
    if (!cameraPosition) {
        console.warn('Failed to get camera position for sync');
        return;
    }

    const response = await fetch(`/api/session/${currentSessionId}/camera`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cameraPosition })
    });

    if (!response.ok) {
        console.error('Failed to sync camera position:', response.status);
    }
}

/**
 * Get session with conversation history
 */
export async function getSessionHistory(): Promise<DiagramSession | null> {
    if (!currentSessionId) {
        return null;
    }

    const response = await fetch(`/api/session/${currentSessionId}`);
    if (!response.ok) {
        return null;
    }

    const data = await response.json();
    return data.session;
}

/**
 * Clear session history
 */
export async function clearSessionHistory(): Promise<void> {
    if (!currentSessionId) {
        return;
    }

    await fetch(`/api/session/${currentSessionId}/history`, {
        method: 'DELETE'
    });
}

/**
 * Get a session preference
 */
export async function getSessionPreference(key: string): Promise<unknown> {
    if (!currentSessionId) {
        return undefined;
    }

    try {
        const response = await fetch(`/api/session/${currentSessionId}/preferences`);
        if (!response.ok) {
            return undefined;
        }
        const data = await response.json();
        return data.preferences?.[key];
    } catch (err) {
        logger.error('Failed to get session preference:', err);
        return undefined;
    }
}

/**
 * Set a session preference
 */
export async function setSessionPreference(key: string, value: unknown): Promise<boolean> {
    if (!currentSessionId) {
        return false;
    }

    try {
        const response = await fetch(`/api/session/${currentSessionId}/preferences`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key, value })
        });
        return response.ok;
    } catch (err) {
        logger.error('Failed to set session preference:', err);
        return false;
    }
}

/**
 * Get token usage for current session
 */
export async function getSessionUsage(): Promise<SessionUsage | null> {
    if (!currentSessionId) {
        return null;
    }

    try {
        const response = await fetch(`/api/session/${currentSessionId}/usage`);
        if (!response.ok) {
            return null;
        }
        return await response.json();
    } catch (err) {
        logger.error('Failed to fetch session usage:', err);
        return null;
    }
}

const SYSTEM_PROMPT = `You are a 3D diagram assistant that EXECUTES actions using tools. You cannot modify the diagram by describing changes - you MUST call tools.

## CRITICAL: YOU MUST CALL TOOLS
**NEVER say "I have modified..." or "The entity has been..." without ACTUALLY calling a tool.**
**If you don't call a tool, NOTHING happens. Your words alone cannot change the diagram.**

When the user requests ANY action:
1. CREATE/ADD/MAKE → CALL create_entity
2. CONNECT/LINK → CALL connect_entities
3. CHANGE/MODIFY/MOVE/RESIZE/SCALE/ROTATE/RENAME → CALL modify_entity
4. CHANGE CONNECTION → CALL modify_connection
5. REMOVE/DELETE → CALL remove_entity
6. LIST/SHOW/WHAT EXISTS → CALL list_entities
7. DIRECTIONS (left, right, forward) → CALL get_camera_position FIRST
8. UNFAMILIAR TOPIC → CALL search_wikipedia FIRST

## WRONG (does nothing):
"I've resized the CDN entity to 0.1m high."

## CORRECT (actually works):
Call modify_entity with: target="CDN", scale={x: 1, y: 0.1, z: 1}

## Scale Parameter
- scale is in METERS (1 = 1 meter, 0.1 = 10cm, 0.5 = 50cm)
- Use {x: width, y: height, z: depth} for non-uniform scaling
- Example: 1m wide × 0.1m tall × 1m deep = scale: {x: 1, y: 0.1, z: 1}

## Connection Labels
When creating connections for the FIRST TIME in a session:
1. Call get_connection_label_preference to check if user has set a preference
2. If no preference is set, ASK the user: "Would you like default labels on connections (e.g., 'Server to Database')?"
3. Save their response with set_connection_label_preference
4. Then proceed with connect_entities

The preference persists for the session - you don't need to ask again.

## Bulk Operations
When the user asks to modify ALL items (e.g., "remove labels from all connections"):
1. First call list_entities to see what exists
2. Then call the modify tool for EACH item that needs to change
3. Use modify_connection with from/to to identify each connection

Example: To remove all connection labels:
- Call list_entities to see connections
- For each connection, call modify_connection with from="EntityA", to="EntityB", label=""

## Research First
When asked to diagram a technical concept, architecture, or system you're not fully familiar with, use search_wikipedia to research it first. This ensures accurate and comprehensive diagrams.

## Available Shapes
box, sphere, cylinder, cone, plane, person

## Available Colors (Toolbox Palette)
ONLY use these exact hex codes. Map any requested color to the closest match:

| Color Name      | Hex Code  |
|-----------------|-----------|
| Black/Dark Gray | #222222   |
| Brown           | #8b4513   |
| Dark Green      | #006400   |
| Slate Gray      | #778899   |
| Purple/Indigo   | #4b0082   |
| Red             | #ff0000   |
| Orange          | #ffa500   |
| Yellow          | #ffff00   |
| Green           | #00ff00   |
| Cyan            | #00ffff   |
| Blue            | #0000ff   |
| Magenta         | #ff00ff   |
| Dodger Blue     | #1e90ff   |
| Pale Green      | #98fb98   |
| Moccasin/Beige  | #ffe4b5   |
| Pink            | #ff69b4   |

Examples of color mapping:
- "navy" → #0000ff (Blue)
- "lime" → #00ff00 (Green)
- "teal" → #00ffff (Cyan)
- "maroon" → #ff0000 (Red)
- "gold" → #ffff00 (Yellow)
- "salmon" → #ff69b4 (Pink)
- "tan" → #ffe4b5 (Moccasin)
- "forest green" → #006400 (Dark Green)

## Coordinate System (from camera/user perspective)
- X axis: LEFT (-) and RIGHT (+) - negative values are to the left, positive to the right
- Y axis: DOWN (-) and UP (+) - 0 is the floor, 1.5 is eye level, higher values go up
- Z axis: BACKWARD (-) and FORWARD (+) - positive values are in front of the camera, negative behind

Example positions:
- (0, 1.5, 2) = directly in front at eye level
- (-1, 1.5, 2) = to the left, at eye level, in front
- (1, 0.5, 2) = to the right, below eye level, in front
- (0, 3, 2) = directly in front, above eye level

## Layout Guidelines
- **Default position**: Place new entities in front of the camera, centered around (0, 1.5, 0) which is eye level
- **Minimum spacing**: Entities must NEVER be closer than 0.2 meters (20cm) from each other
- **Spread layout**: When creating multiple entities, spread them horizontally (x-axis) and vertically (y-axis) with at least 0.5m between them
- Use y=1.5 for eye-level entities (comfortable viewing height)
- Use positive z values (e.g., z=1 to z=3) to place entities in front of the user
- For relative directions (left, right, forward), call get_camera_position first

Be concise. Call tools immediately when the user requests an action.`;

const TOOLS = [
    {
        name: "create_entity",
        description: "Create a 3D shape in the diagram. Use this to add new elements like boxes, spheres, cylinders, etc.",
        input_schema: {
            type: "object",
            properties: {
                shape: {
                    type: "string",
                    enum: ["box", "sphere", "cylinder", "cone", "plane", "person"],
                    description: "The type of 3D shape to create"
                },
                color: {
                    type: "string",
                    description: "Color name (red, blue, green, etc.) or hex code (#ff0000)"
                },
                label: {
                    type: "string",
                    description: "Text label to display on or near the entity"
                },
                position: {
                    type: "object",
                    properties: {
                        x: {type: "number", description: "Left (-) / Right (+) from camera view"},
                        y: {type: "number", description: "Down (-) / Up (+), 0=floor, 1.5=eye level"},
                        z: {type: "number", description: "Backward (-) / Forward (+) from camera view, use positive values (1-3) to place in front"}
                    },
                    description: "3D position. Default around (0, 1.5, 0). Keep entities at least 0.2m apart. Example: (0, 1.5, 2) = in front at eye level"
                }
            },
            required: ["shape"]
        }
    },
    {
        name: "connect_entities",
        description: "Draw a connection line between two entities. Check useDefaultLabels preference first - if not set, ask user if they want default labels on connections.",
        input_schema: {
            type: "object",
            properties: {
                from: {
                    type: "string",
                    description: "ID or label of the source entity"
                },
                to: {
                    type: "string",
                    description: "ID or label of the target entity"
                },
                label: {
                    type: "string",
                    description: "Optional label for the connection. If omitted, default label 'X to Y' is used based on useDefaultLabels preference."
                },
                color: {
                    type: "string",
                    description: "Color of the connection line"
                }
            },
            required: ["from", "to"]
        }
    },
    {
        name: "set_connection_label_preference",
        description: "Set whether connections should have default labels. Call this after asking the user their preference.",
        input_schema: {
            type: "object",
            properties: {
                use_default_labels: {
                    type: "boolean",
                    description: "true = create labels like 'Server to Database', false = no labels on connections"
                }
            },
            required: ["use_default_labels"]
        }
    },
    {
        name: "get_connection_label_preference",
        description: "Check if the user has set a preference for connection labels. Returns the preference or null if not set.",
        input_schema: {
            type: "object",
            properties: {}
        }
    },
    {
        name: "list_entities",
        description: "List all entities currently in the diagram. Use this to see what exists before connecting or modifying.",
        input_schema: {
            type: "object",
            properties: {}
        }
    },
    {
        name: "remove_entity",
        description: "Remove an entity from the diagram by its ID or label.",
        input_schema: {
            type: "object",
            properties: {
                target: {
                    type: "string",
                    description: "ID or label of the entity to remove"
                }
            },
            required: ["target"]
        }
    },
    {
        name: "modify_entity",
        description: "CALL THIS TOOL to modify an entity. You MUST call this tool - describing changes does nothing. Use for: resize, move, rename, recolor, rotate, change shape.",
        input_schema: {
            type: "object",
            properties: {
                target: {
                    type: "string",
                    description: "Label or ID of the entity to modify (e.g., 'CDN', 'Server', 'Database')"
                },
                color: {
                    type: "string",
                    description: "New color hex code from the toolbox palette"
                },
                label: {
                    type: "string",
                    description: "New label text. Use empty string \"\" to remove the label."
                },
                shape: {
                    type: "string",
                    enum: ["box", "sphere", "cylinder", "cone", "plane", "person"],
                    description: "New shape for the entity"
                },
                position: {
                    type: "object",
                    properties: {
                        x: {type: "number", description: "Left(-)/Right(+) in meters"},
                        y: {type: "number", description: "Down(-)/Up(+) in meters"},
                        z: {type: "number", description: "Back(-)/Forward(+) in meters"}
                    },
                    description: "New position in meters"
                },
                scale: {
                    oneOf: [
                        {
                            type: "number",
                            description: "Uniform size in meters (e.g., 1 = 1 meter cube)"
                        },
                        {
                            type: "object",
                            properties: {
                                x: {type: "number", description: "Width in meters"},
                                y: {type: "number", description: "Height in meters"},
                                z: {type: "number", description: "Depth in meters"}
                            },
                            description: "Size as {x: width, y: height, z: depth} in meters. Example: {x: 1, y: 0.1, z: 1} = 1m wide, 10cm tall, 1m deep"
                        }
                    ],
                    description: "Scale/size of the entity. Use a number for uniform scaling or {x, y, z} for non-uniform."
                },
                rotation: {
                    oneOf: [
                        {
                            type: "number",
                            description: "Y-axis rotation in degrees (e.g., 90 = turn right 90°, -90 = turn left 90°)"
                        },
                        {
                            type: "object",
                            properties: {
                                x: {type: "number", description: "Pitch in degrees"},
                                y: {type: "number", description: "Yaw in degrees"},
                                z: {type: "number", description: "Roll in degrees"}
                            },
                            description: "Full 3D rotation in degrees as {x, y, z}"
                        }
                    ],
                    description: "Rotation in degrees. Use a number for Y-axis rotation or {x, y, z} for full 3D rotation."
                }
            },
            required: ["target"]
        }
    },
    {
        name: "modify_connection",
        description: "Modify a connection's label or color. Connections can be identified by their label (e.g., 'Server to Database') or by specifying the from/to entities.",
        input_schema: {
            type: "object",
            properties: {
                target: {
                    type: "string",
                    description: "Label of the connection to modify, or use from/to to identify it"
                },
                from: {
                    type: "string",
                    description: "ID or label of the source entity (alternative to target)"
                },
                to: {
                    type: "string",
                    description: "ID or label of the destination entity (alternative to target)"
                },
                label: {
                    type: "string",
                    description: "New label text for the connection. Use empty string \"\" to remove the label."
                },
                color: {
                    type: "string",
                    description: "New color for the connection"
                }
            }
        }
    },
    {
        name: "clear_diagram",
        description: "DESTRUCTIVE: Permanently delete ALL entities from the diagram and clear the session. This cannot be undone. IMPORTANT: Before calling this tool, you MUST first ask the user to confirm by saying something like 'Are you sure you want to clear the entire diagram? This will permanently delete all entities and cannot be undone.' Only call this tool with confirmed=true AFTER the user explicitly confirms (e.g., says 'yes', 'confirm', 'do it', etc.).",
        input_schema: {
            type: "object",
            properties: {
                confirmed: {
                    type: "boolean",
                    description: "Must be true to execute. Only set to true after user has explicitly confirmed the deletion."
                }
            },
            required: ["confirmed"]
        }
    },
    {
        name: "get_camera_position",
        description: "Get the current camera/viewer position and orientation in the 3D scene. Use this to understand where the user is looking and to position new entities relative to their view. Returns position (x, y, z) and forward direction vector.",
        input_schema: {
            type: "object",
            properties: {}
        }
    },
    {
        name: "list_models",
        description: "List all available AI models that can be used for this conversation.",
        input_schema: {
            type: "object",
            properties: {}
        }
    },
    {
        name: "get_current_model",
        description: "Get information about the currently active AI model.",
        input_schema: {
            type: "object",
            properties: {}
        }
    },
    {
        name: "set_model",
        description: "Change the AI model. Use the model name from list_models.",
        input_schema: {
            type: "object",
            properties: {
                model_id: {
                    type: "string",
                    description: "Model name like 'Claude Opus 4', 'Hermes 2 Pro (CF)', 'Mistral Small 3.1 (CF)', etc."
                }
            },
            required: ["model_id"]
        }
    },
    {
        name: "clear_conversation",
        description: "Clear the conversation history to start fresh. Use this when the conversation has become too long, confusing, or when the user wants to start over. This preserves the diagram entities but clears chat history.",
        input_schema: {
            type: "object",
            properties: {}
        }
    },
    {
        name: "search_wikipedia",
        description: "Search Wikipedia for information about a topic. Use this to research concepts, architectures, technologies, or anything else that would help create more accurate and detailed diagrams. Returns summaries from multiple related articles.",
        input_schema: {
            type: "object",
            properties: {
                topic: {
                    type: "string",
                    description: "The topic or concept to search for (e.g., 'microservices architecture', 'neural network', 'kubernetes')"
                }
            },
            required: ["topic"]
        }
    }
];

interface ClaudeMessage {
    role: 'user' | 'assistant';
    content: string | ClaudeContentBlock[];
}

interface ClaudeContentBlock {
    type: 'text' | 'tool_use' | 'tool_result';
    text?: string;
    id?: string;
    name?: string;
    input?: Record<string, unknown>;
    tool_use_id?: string;
    content?: string;
}

interface ClaudeResponse {
    content: ClaudeContentBlock[];
    stop_reason: 'end_turn' | 'tool_use' | 'max_tokens';
}

async function executeToolCall(toolCall: DiagramToolCall): Promise<ToolResult> {
    logger.debug('[executeToolCall] Executing:', toolCall.name, toolCall.input);
    let result: ToolResult;

    switch (toolCall.name) {
        case 'create_entity':
            result = createEntity(toolCall.input);
            break;
        case 'connect_entities': {
            // Check if label is explicitly provided (including empty string)
            const hasExplicitLabel = toolCall.input.label !== undefined;
            let label = toolCall.input.label;

            // If no explicit label, check preference
            if (!hasExplicitLabel) {
                const useDefaultLabels = await getSessionPreference('useDefaultConnectionLabels');
                if (useDefaultLabels === false) {
                    // User prefers no labels - pass empty string
                    label = '';
                }
                // If true or undefined, let connectEntities create default label (pass undefined)
            }

            result = await connectEntities({
                ...toolCall.input,
                label
            });
            break;
        }
        case 'remove_entity':
            result = await removeEntity(toolCall.input);
            break;
        case 'modify_entity':
            result = await modifyEntity(toolCall.input);
            break;
        case 'modify_connection':
            result = await modifyConnection(toolCall.input);
            break;
        case 'list_entities':
            result = await listEntities();
            break;
        case 'clear_diagram':
            result = await clearDiagram(toolCall.input);
            // If successful, also clear the session (history and entities)
            if (result.success && currentSessionId) {
                await clearSessionHistory();
                // Sync empty entity list to clear server-side entity cache
                await syncEntitiesToSession([]);
            }
            break;
        case 'get_camera_position':
            result = await getCameraPosition();
            break;
        case 'list_models': {
            const models = getAvailableModels();
            const current = getCurrentModel();
            const modelList = models.map(m =>
                `• ${m.name}${m.id === current.id ? ' [CURRENT]' : ''}\n  ${m.description}`
            ).join('\n\n');
            result = {
                toolName: 'list_models',
                success: true,
                message: `Available models:\n\n${modelList}\n\nTo switch, use set_model with the model name (e.g., "Hermes 2 Pro (CF)")`
            };
            break;
        }
        case 'get_current_model': {
            const model = getCurrentModel();
            result = {
                toolName: 'get_current_model',
                success: true,
                message: `Current model: ${model.name} (${model.id})\n${model.description}`
            };
            break;
        }
        case 'set_model': {
            // Normalize parameter name - some models use model_name instead of model_id
            const modelId = toolCall.input.model_id || toolCall.input.model_name || toolCall.input.modelId || toolCall.input.model;
            const success = setCurrentModel(modelId);
            if (success) {
                const model = getCurrentModel();
                result = {
                    toolName: 'set_model',
                    success: true,
                    message: `Model changed to: ${model.name}\n${model.description}\n\nNote: The new model will be used starting from the next message.`
                };
            } else {
                const models = getAvailableModels();
                result = {
                    toolName: 'set_model',
                    success: false,
                    message: `Invalid model ID: "${modelId}"\n\nAvailable models: ${models.map(m => m.id).join(', ')}`
                };
            }
            break;
        }
        case 'clear_conversation': {
            const cleared = await clearConversationHistory();
            result = {
                toolName: 'clear_conversation',
                success: cleared,
                message: cleared
                    ? 'Conversation history cleared. Starting fresh while keeping the diagram intact.'
                    : 'Failed to clear conversation history (no active session).'
            };
            break;
        }
        case 'search_wikipedia': {
            const searchResult = await searchWikipedia(toolCall.input.topic);
            result = {
                toolName: 'search_wikipedia',
                success: searchResult.success,
                message: searchResult.message
            };
            break;
        }
        case 'set_connection_label_preference': {
            const useDefaultLabels = toolCall.input.use_default_labels;
            const success = await setSessionPreference('useDefaultConnectionLabels', useDefaultLabels);
            result = {
                toolName: 'set_connection_label_preference',
                success,
                message: success
                    ? `Connection label preference set: ${useDefaultLabels ? 'Default labels will be created (e.g., "Server to Database")' : 'No labels will be created on connections'}`
                    : 'Failed to save preference (no active session)'
            };
            break;
        }
        case 'get_connection_label_preference': {
            const pref = await getSessionPreference('useDefaultConnectionLabels');
            if (pref === undefined) {
                result = {
                    toolName: 'get_connection_label_preference',
                    success: true,
                    message: 'No preference set yet. Ask the user if they want default labels on connections.'
                };
            } else {
                result = {
                    toolName: 'get_connection_label_preference',
                    success: true,
                    message: `Connection label preference: ${pref ? 'Default labels enabled' : 'No labels on connections'}`
                };
            }
            break;
        }
        default:
            result = {
                toolName: 'unknown',
                success: false,
                message: 'Unknown tool'
            };
    }

    logger.debug('[executeToolCall] Result:', result);
    return result;
}

/**
 * Clear conversation history on the server
 */
export async function clearConversationHistory(): Promise<boolean> {
    if (!currentSessionId) {
        logger.warn('[clearConversationHistory] No active session');
        return false;
    }

    try {
        const response = await fetch(`/api/session/${currentSessionId}/history`, {
            method: 'DELETE'
        });

        if (response.ok) {
            logger.info('[clearConversationHistory] History cleared for session:', currentSessionId);
            return true;
        } else {
            logger.error('[clearConversationHistory] Failed:', response.status);
            return false;
        }
    } catch (error) {
        logger.error('[clearConversationHistory] Error:', error);
        return false;
    }
}

/**
 * Search Wikipedia for a topic using multiple query variations
 * Returns combined results for context
 */
async function searchWikipedia(topic: string): Promise<{ success: boolean; message: string }> {
    logger.info('[searchWikipedia] Searching for:', topic);

    // Generate 3 search variations
    const searchQueries = [
        topic,
        `${topic} overview`,
        `${topic} definition concept`
    ];

    const results: string[] = [];

    for (const query of searchQueries) {
        try {
            // Search Wikipedia API
            const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(query)}&srlimit=2&format=json&origin=*`;
            const searchResponse = await fetch(searchUrl);
            const searchData = await searchResponse.json();

            if (searchData.query?.search?.length > 0) {
                // Get summaries for top results
                for (const result of searchData.query.search.slice(0, 2)) {
                    const title = result.title;

                    // Skip if we already have this article
                    if (results.some(r => r.includes(`**${title}**`))) {
                        continue;
                    }

                    // Get article summary
                    const summaryUrl = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`;
                    const summaryResponse = await fetch(summaryUrl);

                    if (summaryResponse.ok) {
                        const summaryData = await summaryResponse.json();
                        const extract = summaryData.extract || 'No summary available.';
                        // Truncate long summaries
                        const truncated = extract.length > 500 ? extract.substring(0, 500) + '...' : extract;
                        results.push(`**${title}**\n${truncated}`);
                    }
                }
            }
        } catch (error) {
            logger.warn('[searchWikipedia] Error searching for:', query, error);
        }
    }

    if (results.length === 0) {
        return {
            success: false,
            message: `No Wikipedia articles found for "${topic}". Try a different search term.`
        };
    }

    // Combine results
    const combinedResults = results.slice(0, 5).join('\n\n---\n\n');

    return {
        success: true,
        message: `## Wikipedia Research: ${topic}\n\nFound ${results.length} relevant articles:\n\n${combinedResults}\n\n---\nUse this information to create accurate diagrams. You can now create entities based on this research.`
    };
}

export async function sendMessage(
    userMessage: string,
    conversationHistory: ChatMessage[],
    onToolResult?: (result: ToolResult) => void
): Promise<{ response: string; toolResults: ToolResult[] }> {
    logger.debug('[sendMessage] Starting with message:', userMessage);
    logger.debug('[sendMessage] Session ID:', currentSessionId);

    // Sync camera position before sending message so AI knows user's view
    await syncCameraToSession();

    // When using sessions, we don't need to send full history - server manages it
    // Just send the new message
    const messages: ClaudeMessage[] = currentSessionId
        ? [{ role: 'user', content: userMessage }]
        : conversationHistory
            .filter(m => !m.isLoading)
            .map(m => ({
                role: m.role,
                content: m.content
            }));

    if (!currentSessionId) {
        messages.push({role: 'user', content: userMessage});
    }

    logger.debug('[sendMessage] Messages to send:', messages.length);

    const allToolResults: ToolResult[] = [];
    let finalResponse = '';
    let continueLoop = true;
    let loopCount = 0;
    const MAX_TOOL_ITERATIONS = 10; // Safety limit to prevent infinite loops

    while (continueLoop) {
        loopCount++;
        logger.debug(`[sendMessage] Loop iteration ${loopCount}`);

        // Safety check: prevent infinite tool calling loops
        if (loopCount > MAX_TOOL_ITERATIONS) {
            logger.warn(`[sendMessage] Max tool iterations (${MAX_TOOL_ITERATIONS}) reached, breaking loop`);
            break;
        }

        const requestBody = {
            model: currentModelId,
            max_tokens: 4096,
            system: SYSTEM_PROMPT,
            tools: TOOLS,
            messages,
            ...(currentSessionId && { sessionId: currentSessionId })
        };

        logger.debug('[sendMessage] Request body:', JSON.stringify(requestBody, null, 2).substring(0, 500) + '...');

        const apiEndpoint = getApiEndpoint();
        logger.debug('[sendMessage] Using API endpoint:', apiEndpoint);

        const response = await fetch(apiEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody)
        });

        logger.debug('[sendMessage] Response status:', response.status);

        if (!response.ok) {
            const errorText = await response.text();
            logger.error('[sendMessage] API error:', response.status, errorText);
            throw new Error(`API error: ${response.status} - ${errorText}`);
        }

        const data: ClaudeResponse = await response.json();
        logger.debug('[sendMessage] Response data:', JSON.stringify(data, null, 2).substring(0, 500) + '...');
        logger.debug('[sendMessage] Stop reason:', data.stop_reason);

        const textBlocks = data.content.filter(b => b.type === 'text');
        const toolBlocks = data.content.filter(b => b.type === 'tool_use');

        logger.debug('[sendMessage] Text blocks:', textBlocks.length, 'Tool blocks:', toolBlocks.length);

        if (textBlocks.length > 0) {
            finalResponse = textBlocks.map(b => b.text).join('\n');
            logger.debug('[sendMessage] Final response:', finalResponse.substring(0, 200) + '...');
        }

        if (data.stop_reason === 'tool_use' && toolBlocks.length > 0) {
            logger.debug('[sendMessage] Processing tool calls...');
            messages.push({
                role: 'assistant',
                content: data.content
            });

            const toolResults: ClaudeContentBlock[] = [];
            let modelSwitched = false;

            for (const toolBlock of toolBlocks) {
                console.log('[sendMessage] Full tool block:', JSON.stringify(toolBlock, null, 2));
                logger.debug('[sendMessage] Tool call:', toolBlock.name, JSON.stringify(toolBlock.input));
                const toolCall: DiagramToolCall = {
                    name: toolBlock.name as DiagramToolCall['name'],
                    input: toolBlock.input as DiagramToolCall['input']
                };

                // Track if we're switching models
                if (toolBlock.name === 'set_model') {
                    modelSwitched = true;
                }

                const result = await executeToolCall(toolCall);
                logger.debug('[sendMessage] Tool result:', result);
                allToolResults.push(result);
                onToolResult?.(result);

                toolResults.push({
                    type: 'tool_result',
                    tool_use_id: toolBlock.id,
                    content: result.message
                });
            }

            messages.push({
                role: 'user',
                content: toolResults
            });

            // If model was switched, break the loop - the new model won't have proper context
            // and continuing could cause infinite loops with Cloudflare models
            if (modelSwitched) {
                logger.debug('[sendMessage] Model switched, ending loop to prevent context issues');
                // Set a response so user knows what happened
                if (!finalResponse) {
                    const model = getCurrentModel();
                    finalResponse = `Switched to ${model.name}. The new model is now active for your next message.`;
                }
                continueLoop = false;
            } else {
                logger.debug('[sendMessage] Added tool results, continuing loop...');
            }
        } else {
            logger.debug('[sendMessage] No more tool calls, ending loop');
            continueLoop = false;
        }
    }

    logger.debug('[sendMessage] Complete. Final response length:', finalResponse.length, 'Tool results:', allToolResults.length);
    return {response: finalResponse, toolResults: allToolResults};
}

export function createUserMessage(content: string): ChatMessage {
    return {
        id: uuidv4(),
        role: 'user',
        content,
        timestamp: new Date()
    };
}

export function createAssistantMessage(content: string, toolResults?: ToolResult[]): ChatMessage {
    return {
        id: uuidv4(),
        role: 'assistant',
        content,
        timestamp: new Date(),
        toolResults
    };
}

export function createLoadingMessage(): ChatMessage {
    return {
        id: uuidv4(),
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isLoading: true
    };
}
